import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  ButtonGroup,
  Popover,
  Stack,
  TextField
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import moment from 'moment-timezone';
import { getStoredUserTimezone } from 'src/utils/timezoneUtils';

export const DATE_TIME_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

export const RANGE_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7D' },
  { id: '30d', label: '30D' }
];

const getPresetRange = (presetId, timezoneName) => {
  const now = moment().tz(timezoneName).seconds(0).milliseconds(0);
  const todayStart = now.clone().startOf('day');

  if (presetId === 'yesterday') {
    const yesterday = todayStart.clone().subtract(1, 'day');
    return {
      from: yesterday.format(DATE_TIME_INPUT_FORMAT),
      to: yesterday.clone().endOf('day').format(DATE_TIME_INPUT_FORMAT)
    };
  }

  if (presetId === '7d' || presetId === '30d') {
    return {
      from: now.clone().subtract(presetId === '7d' ? 7 : 30, 'days').format(DATE_TIME_INPUT_FORMAT),
      to: now.format(DATE_TIME_INPUT_FORMAT)
    };
  }

  return {
    from: todayStart.format(DATE_TIME_INPUT_FORMAT),
    to: now.format(DATE_TIME_INPUT_FORMAT)
  };
};

const toUnixSeconds = (value, timezoneName) => {
  const parsed = moment.tz(value, DATE_TIME_INPUT_FORMAT, true, timezoneName);
  return parsed.isValid() ? parsed.unix() : null;
};

export const useTankDateRange = () => {
  const timezoneName = getStoredUserTimezone();
  const initialRange = getPresetRange('today', timezoneName);
  const [dateRange, setDateRange] = useState(initialRange);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [validationError, setValidationError] = useState('');

  const handleDateRangeChange = useCallback((field) => (event) => {
    setDateRange((current) => ({ ...current, [field]: event.target.value }));
    setValidationError('');
  }, []);

  const handlePresetChange = useCallback((presetId) => {
    const nextRange = getPresetRange(presetId, timezoneName);
    setDateRange(nextRange);
    setAppliedRange(nextRange);
    setSelectedPreset(presetId);
    setValidationError('');
  }, [timezoneName]);

  const handleApply = useCallback(() => {
    const timeBegin = toUnixSeconds(dateRange.from, timezoneName);
    const timeEnd = toUnixSeconds(dateRange.to, timezoneName);
    if (timeBegin === null || timeEnd === null) {
      setValidationError('Enter a valid start and end date-time.');
      return false;
    }
    if (timeBegin >= timeEnd) {
      setValidationError('End date-time must be after start date-time.');
      return false;
    }
    setValidationError('');
    setAppliedRange({ ...dateRange });
    setSelectedPreset(null);
    return true;
  }, [dateRange, timezoneName]);

  const handleCancel = useCallback(() => {
    setDateRange({ ...appliedRange });
    setValidationError('');
  }, [appliedRange]);

  const parseToUnixSeconds = useCallback(
    (value) => toUnixSeconds(value, timezoneName),
    [timezoneName]
  );

  return {
    timezoneName,
    dateRange,
    appliedRange,
    selectedPreset,
    validationError,
    handleDateRangeChange,
    handlePresetChange,
    handleApply,
    handleCancel,
    toUnixSeconds: parseToUnixSeconds
  };
};

export const tankDateRangeStatePropType = PropTypes.shape({
  timezoneName: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired
  }).isRequired,
  appliedRange: PropTypes.shape({
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired
  }).isRequired,
  selectedPreset: PropTypes.string,
  validationError: PropTypes.string,
  handleDateRangeChange: PropTypes.func.isRequired,
  handlePresetChange: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  toUnixSeconds: PropTypes.func.isRequired
}).isRequired;

const TankDateRangeFilter = ({
  dateRange,
  selectedPreset,
  validationError,
  onDateRangeChange,
  onPresetChange,
  onApply,
  onCancel
}) => {
  const [customRangeAnchor, setCustomRangeAnchor] = useState(null);
  const isCustomRangeOpen = Boolean(customRangeAnchor);

  const handleCustomRangeApply = () => {
    if (onApply()) setCustomRangeAnchor(null);
  };

  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="flex-end"
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <ButtonGroup
          size="small"
          variant="outlined"
          aria-label="Tank date range presets"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {RANGE_OPTIONS.map((option) => (
            <Button
              key={option.id}
              variant={selectedPreset === option.id ? 'contained' : 'outlined'}
              onClick={() => onPresetChange(option.id)}
              sx={{ textTransform: 'none', fontWeight: 700, flex: { xs: 1, sm: 'initial' } }}
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>
        <Button
          variant={selectedPreset === null ? 'contained' : 'outlined'}
          startIcon={<CalendarMonthIcon />}
          onClick={(event) => setCustomRangeAnchor(event.currentTarget)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            minHeight: 40
          }}
        >
          Custom Range
        </Button>
      </Stack>

      <Popover
        open={isCustomRangeOpen}
        anchorEl={customRangeAnchor}
        onClose={() => setCustomRangeAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, width: { xs: 320, sm: 520 }, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField label="From" type="datetime-local" value={dateRange.from} onChange={onDateRangeChange('from')} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="datetime-local" value={dateRange.to} onChange={onDateRangeChange('to')} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
          {validationError && <Alert severity="warning">{validationError}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => { onCancel(); setCustomRangeAnchor(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={handleCustomRangeApply} sx={{ textTransform: 'none', fontWeight: 700 }}>Apply</Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};

TankDateRangeFilter.propTypes = {
  dateRange: PropTypes.shape({ from: PropTypes.string.isRequired, to: PropTypes.string.isRequired }).isRequired,
  selectedPreset: PropTypes.string,
  validationError: PropTypes.string,
  onDateRangeChange: PropTypes.func.isRequired,
  onPresetChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

TankDateRangeFilter.defaultProps = {
  selectedPreset: null,
  validationError: ''
};

export default TankDateRangeFilter;
